import React from 'react';
import { post } from '../../../../api/baseUrl';
import {
  toTagTO,
  type GetPreferencesPayload,
  type GetPreferencesResponse,
  type UpdatePreferencesPayload,
} from '../../../../lib';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import { setPreferences } from '../../../../store/ai2';
import { useGlobalLoading } from '../../../../context';
import { toPathologyTO } from '../../../../lib/transferObjects/Pathology';

const getPreferencesEndpoint = '/getPreferences';

const updatePreferencesEndpoint = '/updatePreferences';

const usePreferences = () => {
  const { setLoadingPreferences } = useGlobalLoading();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const { initialized } = useSelector((state: RootState) => state.preferences);
  const dispatch = useDispatch();

  const getTags = React.useCallback(async () => {
    if (!selectedTenancy || !selectedTenancy?.tenancy_id) return;
    setLoadingPreferences(true);
    const body: GetPreferencesPayload = {
      tenancyId: selectedTenancy.tenancy_id,
      preferenceType: 'hiddenTags',
    };
    const {
      data: { data },
    } = await post(getPreferencesEndpoint, body);
    const preferences = (data as GetPreferencesResponse)?.preferences;
    if (preferences) {
      return { ...preferences };
    } else {
      return { tags: [], hiddenTags: [] };
    }
  }, [selectedTenancy]);

  const getPathologies = React.useCallback(async () => {
    if (!selectedTenancy || !selectedTenancy?.tenancy_id) return;
    const body: GetPreferencesPayload = {
      tenancyId: selectedTenancy.tenancy_id,
      preferenceType: 'hiddenPathologies',
    };
    const {
      data: { data },
    } = await post(getPreferencesEndpoint, body);
    const preferences = (data as GetPreferencesResponse)?.preferences;
    if (preferences) {
      return { ...preferences };
    } else {
      return { pathologies: [], hiddenPathologies: [] };
    }
  }, [selectedTenancy]);

  const getHiddenTasks = React.useCallback(async () => {
    if (!selectedTenancy || !selectedTenancy?.tenancy_id) return;
    try {
      setLoadingPreferences(true);
      const body: GetPreferencesPayload = {
        tenancyId: selectedTenancy.tenancy_id,
        preferenceType: 'hiddenTasks',
      };
      const {
        data: { data },
      } = await post(getPreferencesEndpoint, body);
      const preferences = (data as GetPreferencesResponse)?.preferences;
      if (preferences) {
        return { ...preferences };
      } else {
        return { hiddenTasks: [] };
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPreferences(false);
    }
  }, [selectedTenancy]);

  const getAllPreferences = async () => {
    try {
      setLoadingPreferences(true);
      const [tagsResponse, pathologiesResponse, tasksResponse] =
        await Promise.all([getTags(), getPathologies(), getHiddenTasks()]);
      const payload = initialized
        ? {
            hiddenTags: tagsResponse?.hiddenTags || [],
            tags: tagsResponse?.tags.map(toTagTO),
            pathologies: pathologiesResponse?.pathologies || [],
            hiddenPathologies: pathologiesResponse?.hiddenPathologies || [],
            hiddenTasks: tasksResponse?.hiddenTasks || [],
          }
        : {
            hiddenTags: tagsResponse?.hiddenTags || [],
            tags: tagsResponse?.tags.map(toTagTO),
            pathologies: pathologiesResponse?.pathologies || [],
            hiddenPathologies: pathologiesResponse?.hiddenPathologies || [],
            allPathologies:
              pathologiesResponse?.pathologies.map(toPathologyTO) || [],
            allTags: tagsResponse?.tags.map(toTagTO),
            hiddenTasks: tasksResponse?.hiddenTasks || [],
            initialized: true,
          };
      dispatch(setPreferences(payload));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPreferences(false);
    }
  };

  const updatePathologyPreferences = React.useCallback(
    async (selectedPathologies?: string[]) => {
      if (!selectedPathologies || !selectedTenancy) return;
      try {
        const body: UpdatePreferencesPayload = {
          tenancyId: selectedTenancy.tenancy_id,
          preferenceType: 'hiddenPathologies',
          preferenceValues: selectedPathologies,
        };
        const { data } = await post(updatePreferencesEndpoint, body);
        if (data.data.success) {
          dispatch(setPreferences({ hiddenPathologies: selectedPathologies }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    [],
  );

  const updateTaskPreferences = React.useCallback(
    async (selectedTasks?: string[]) => {
      if (!selectedTasks || !selectedTenancy) return;
      try {
        const body: UpdatePreferencesPayload = {
          tenancyId: selectedTenancy.tenancy_id,
          preferenceType: 'hiddenTasks',
          preferenceValues: selectedTasks,
        };
        const { data } = await post(updatePreferencesEndpoint, body);
        if (data.data.success) {
          dispatch(setPreferences({ hiddenTasks: selectedTasks }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    [],
  );
  return {
    updatePathologyPreferences,
    getTags,
    getPathologies,
    getAllPreferences,
    updateTaskPreferences,
    initialized,
  };
};

export default usePreferences;
